import { ApiOptions, Maybe } from '@csp/csp-common-model';
import { getMessage } from '@csp/csp-common-util';
import { toRestOptions } from '@csp/csp-fe-auth';
import { DeviceSessionsRestServiceV1 } from '@csp/dmdp-api-client';
import { Page } from '@csp/dmdp-api-common-dto';
import {
  DeviceSessionIdOrErrorV1,
  DeviceSessionIdsOrErrorsV1,
  DeviceSessionInAndIdV1,
  DeviceSessionInV1,
  DeviceSessionQueryV1,
  DeviceSessionV1,
} from '@csp/dmdp-api-device-dto';
import { chunk, map } from 'lodash';
import { DeviceSession } from '../model/DeviceSession';
import { DeviceSessionQueryCriteria } from '../model/DeviceSessionQueryCriteria';
import { SessionsQueryResponse } from '../model/SessionsQueryResponse';
import { DeviceSessionQueryFactory } from './DeviceSessionQueryFactory';

const BATCH_SIZE = 500;
const SIMULTANEOUS_BATCH_SIZE = 100;
const SIMULTANEOUS_BATCH_DELAY = 1000;

const toDeviceSessions = (sessions: DeviceSessionV1[]): DeviceSession[] => map(sessions, DeviceSession.from);

const createDeviceSession = async (
  deviceSessionInV1: DeviceSessionInV1,
  apiOptions?: ApiOptions,
): Promise<DeviceSession> => {
  const sessionV1 = await DeviceSessionsRestServiceV1.createDeviceSession(toRestOptions(apiOptions), deviceSessionInV1);
  return DeviceSession.from(sessionV1);
};

const batchDeviceSessions = async (
  deviceSessionsInAndIdV1: DeviceSessionInAndIdV1[],
  apiOptions?: ApiOptions,
): Promise<DeviceSessionIdsOrErrorsV1> => {
  const chunks = chunk(deviceSessionsInAndIdV1, BATCH_SIZE);
  const result = await Promise.all(
    chunks.map(chunk =>
      DeviceSessionsRestServiceV1.batchDeviceSessions(
        toRestOptions(apiOptions),
        {
          deviceSessions: chunk,
        },
        { count: false } as Page,
      ),
    ),
  );
  const flattenedList = result.flatMap(list => list.deviceSessionIdOrErrors);
  return { deviceSessionIdOrErrors: flattenedList };
};

const createDeviceSessionForUser = async (
  userId: string,
  deviceSessionInAndIdV1: DeviceSessionInAndIdV1,
  apiOptions?: ApiOptions,
): Promise<DeviceSessionIdOrErrorV1> => {
  try {
    const sessionV1 = await DeviceSessionsRestServiceV1.createDeviceSessionForUser(
      toRestOptions(apiOptions),
      userId,
      deviceSessionInAndIdV1.deviceSession,
    );
    return {
      correlationId: deviceSessionInAndIdV1.correlationId,
      deviceSessionId: sessionV1.deviceSessionId,
    };
  } catch (error) {
    return {
      correlationId: deviceSessionInAndIdV1.correlationId,
      error: { message: getMessage(error) },
    };
  }
};

const batchDeviceSessionsForUser = async (
  userId: string,
  deviceSessionsInAndIdV1: DeviceSessionInAndIdV1[],
  apiOptions?: ApiOptions,
): Promise<DeviceSessionIdsOrErrorsV1> => {
  const deviceSessionIdOrErrors: DeviceSessionIdOrErrorV1[] = [];
  const chunks = chunk(deviceSessionsInAndIdV1, SIMULTANEOUS_BATCH_SIZE);

  for (const [index, chunk] of chunks.entries()) {
    if (index !== 0) {
      await new Promise(resolve => setTimeout(resolve, SIMULTANEOUS_BATCH_DELAY));
    }
    deviceSessionIdOrErrors.push(
      ...(await Promise.all(
        chunk.map(deviceSessionInAndIdV1 => createDeviceSessionForUser(userId, deviceSessionInAndIdV1, apiOptions)),
      )),
    );
  }

  return { deviceSessionIdOrErrors };
};

const getSessionById = async (deviceSessionId: string, apiOptions?: ApiOptions): Promise<Maybe<DeviceSession>> => {
  const sessionV1 = await DeviceSessionsRestServiceV1.getDeviceSession(toRestOptions(apiOptions), deviceSessionId);
  return sessionV1 ? DeviceSession.from(sessionV1) : undefined;
};

const queryUserDeviceSessions = async (
  query: DeviceSessionQueryV1,
  page: Page,
  apiOptions?: ApiOptions,
): Promise<SessionsQueryResponse> => {
  const { sessions, paging = {} } = await DeviceSessionsRestServiceV1.queryDeviceSessions(
    toRestOptions(apiOptions),
    query,
    page,
  );

  return {
    sessions: sessions ? toDeviceSessions(sessions) : [],
    paging,
  };
};

const queryAllUserDeviceSessions = async (
  queryCriteria: DeviceSessionQueryCriteria,
  apiOptions?: ApiOptions,
): Promise<DeviceSession[]> => {
  const allDeviceSessions: DeviceSession[] = [];

  const criteria = DeviceSessionQueryFactory.filterByDeviceSessionQueryCriteria(queryCriteria);

  const pageSize = 500;
  const firstPage = await queryUserDeviceSessions(
    {
      criterion: criteria,
    },
    {
      limit: pageSize,
      count: true,
      next: undefined,
    },
    apiOptions,
  );
  allDeviceSessions.push(...firstPage.sessions);

  let amountOfPagesLeft = Math.ceil((firstPage.paging.count ?? 0) / pageSize) - 1;
  let nextStr = firstPage.paging.next;
  while (0 < amountOfPagesLeft--) {
    const page = await queryUserDeviceSessions(
      {
        criterion: criteria,
      },
      {
        limit: pageSize,
        count: false,
        next: nextStr,
      },
      apiOptions,
    );
    nextStr = page.paging.next;
    allDeviceSessions.push(...page.sessions);
  }

  return allDeviceSessions;
};

export const DeviceSessionService = {
  createDeviceSession,
  batchDeviceSessions,
  batchDeviceSessionsForUser,
  getSessionById,
  queryUserDeviceSessions,
  queryAllUserDeviceSessions,
};
